"use client";

import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Check, CircleAlert as AlertCircle, TrendingUp, ArrowRight, Building2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const industriesData = [{
  id: "plumbing",
  label: "PLUMBING & PIPING",
  title: "Plumbing & Emergency Services",
  failureReason: "Over 68% of urgent local plumbing searches now happen via AI voice and assistant queries. Websites without structured schema and clear service areas are completely ignored by search LLMs.",
  benefit1: "Automated schema markup for 24/7 emergency availability and service radiuses",
  benefit2: "Instant review capture system building verified local trust signals",
  benefit3: "Direct AI indexing across ChatGPT, Perplexity, and Apple Intelligence",
  outcome: "+310% increase in high-intent AI recommendations for urgent repairs."
}, {
  id: "hvac",
  label: "HVAC & CLIMATE",
  title: "Heating & Air Conditioning",
  failureReason: "Homeowners asking AI for quote comparisons or seasonal furnace tune-ups are routed to competitors whose pricing structures and certifications are explicitly indexed.",
  benefit1: "Structured service menus and seasonal maintenance package highlights",
  benefit2: "Real-time AI citation mapping for local service coverage zones",
  benefit3: "Fast inquiry routing for high-value HVAC system replacements",
  outcome: "4.2x higher conversion rate on AI voice & search referrals."
}, {
  id: "dental",
  label: "DENTAL & MEDICAL",
  title: "Dental Practices & Clinics",
  failureReason: "Patients using AI to find 'gentle cosmetic dentists accepting new patients' get generic directory results because key clinic differentiators aren't readable by LLM engines.",
  benefit1: "Structured provider bios, service lists, and insurance acceptance data",
  benefit2: "Clean review distribution across Google, Healthgrades, and local directories",
  benefit3: "Direct online booking link placement inside AI search answer cards",
  outcome: "Top 3 placement in local medical AI assistant answers."
}, {
  id: "legal",
  label: "LEGAL SERVICES",
  title: "Local Law Firms & Attorneys",
  failureReason: "AI search engines prioritize firms with verified case focus areas and high-authority citations. Unoptimized legal sites get buried beneath generic lead aggregator sites.",
  benefit1: "Practice area structured data and authority citation optimization",
  benefit2: "Reputation system collecting genuine client feedback automatically",
  benefit3: "Monthly AI ranking breakdown comparing your firm against local rivals",
  outcome: "58% reduction in cost-per-lead by capturing non-ad AI traffic."
}, {
  id: "roofing",
  label: "ROOFING & EXTERIORS",
  title: "Roofing & Exterior Contractors",
  failureReason: "Storm damage inquiries and emergency roof repair requests are answered by generative AI assistants that require strict geotagged project proof to recommend contractors.",
  benefit1: "Geotagged project schema and storm response landing page structure",
  benefit2: "Review capture triggers sent immediately upon project completion",
  benefit3: "LLM visibility checks for storm-affected local zip codes",
  outcome: "$140k+ estimated extra annual pipeline from organic AI referrals."
}];
export default function Industries() {
  const [selectedId, setSelectedId] = useState<string>(industriesData[0].id);
  const shouldReduceMotion = useReducedMotion();
  const selectedIndustry = industriesData.find(item => item.id === selectedId) || industriesData[0];
  return <section id="industriesbenefitfilter" data-nav="light" className="w-full bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div className="max-w-6xl mx-auto space-y-12">
        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs uppercase tracking-widest text-[var(--accent-deep-red)] border-[var(--accent-deep-red)]/30 px-3 py-1">
              Industry Breakdown
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground">
            How Local Services Win in AI Search
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Select your industry below to see why traditional sites fail when potential clients ask AI who to hire—and how ClearLine fixes it.
          </p>
        </motion.div>

        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 pb-2">
          {industriesData.map((item, i) => {
          const isActive = item.id === selectedId;
          return <Button key={item.id} data-index={i} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setSelectedId(item.id)} className={`font-mono text-xs uppercase tracking-wider h-10 px-4 transition-all duration-200 border ${isActive ? "bg-[var(--accent-deep-red)] text-white border-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 shadow-sm" : "bg-background text-foreground border-border hover:border-[var(--accent-deep-red)]/60 hover:text-[var(--accent-deep-red)]"}`}>
                <Building2 className="w-3.5 h-3.5 mr-1.5 opacity-70" />
                {item.label}
              </Button>;
        })}
        </motion.div>

        {}
        <div className="min-h-[420px]">
          <AnimatePresence mode="wait">
            <motion.div key={selectedIndustry.id} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={shouldReduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.35,
            ease: "easeInOut"
          }}>
              <Card className="bg-card text-card-foreground border border-border shadow-md overflow-hidden">
                <CardContent className="p-6 sm:p-8 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                  
                  {}
                  <div className="lg:col-span-5 space-y-6">
                    <div>
                      <span className="font-mono text-xs uppercase text-[var(--accent-deep-red)] tracking-widest block mb-2">
                        // Tailored Strategy
                      </span>
                      <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground">
                        {selectedIndustry.title}
                      </h3>
                    </div>

                    <div className="p-4 rounded-md bg-[var(--accent-deep-red)]/10 border border-[var(--accent-deep-red)]/20 space-y-2">
                      <div className="flex items-center gap-2 text-[var(--accent-deep-red)] font-mono text-xs uppercase font-semibold">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>Why Standard Sites Fail AI</span>
                      </div>
                      <p className="text-sm text-foreground/90 leading-relaxed">
                        {selectedIndustry.failureReason}
                      </p>
                    </div>

                    <div className="pt-2">
                      <Button asChild variant="default" className="bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white font-medium text-sm">
                        <Link to="/VisibilityCheckupPage">
                          Check Your Visibility
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                  {}
                  <div className="lg:col-span-7 space-y-6 lg:border-l lg:border-border lg:pl-10">
                    <div>
                      <h4 className="font-mono text-xs uppercase text-muted-foreground tracking-widest mb-4">
                        ClearLine System Improvements
                      </h4>
                      <ul className="space-y-4">
                        <li data-index={0} className="flex items-start gap-3">
                          <div className="p-1 rounded-sm bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] shrink-0 mt-0.5 border border-[var(--accent-deep-red)]/20">
                            <Check className="w-4 h-4" />
                          </div>
                          <span className="text-sm sm:text-base text-foreground">
                            {selectedIndustry.benefit1}
                          </span>
                        </li>
                        <li data-index={1} className="flex items-start gap-3">
                          <div className="p-1 rounded-sm bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] shrink-0 mt-0.5 border border-[var(--accent-deep-red)]/20">
                            <Check className="w-4 h-4" />
                          </div>
                          <span className="text-sm sm:text-base text-foreground">
                            {selectedIndustry.benefit2}
                          </span>
                        </li>
                        <li data-index={2} className="flex items-start gap-3">
                          <div className="p-1 rounded-sm bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] shrink-0 mt-0.5 border border-[var(--accent-deep-red)]/20">
                            <Check className="w-4 h-4" />
                          </div>
                          <span className="text-sm sm:text-base text-foreground">
                            {selectedIndustry.benefit3}
                          </span>
                        </li>
                      </ul>
                    </div>

                    <Separator className="bg-border my-6" />

                    {}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 font-mono text-xs uppercase text-[var(--accent-deep-red)] tracking-wider font-semibold">
                        <TrendingUp className="w-4 h-4" />
                        <span>Typical Outcome</span>
                      </div>
                      <p className="text-base sm:text-lg font-serif font-medium text-foreground">
                        {selectedIndustry.outcome}
                      </p>
                    </div>
                  </div>

                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}